import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { MailCheck } from 'lucide-react';
import AuthLayout from '@/components/AuthLayout';
import { authApi } from '@/services/auth';
import { ApiError } from '@/lib/api-client';

const formSchema = z.object({
    email: z
        .string()
        .min(1, 'Email is required')
        .email('Please enter a valid email address'),
});

type FormValues = z.infer<typeof formSchema>;

export default function ForgotPasswordPage() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: '',
        },
    });

    const onSubmit = async (data: FormValues) => {
        try {
            setIsSubmitting(true);

            // Call the real forgot password API
            const response = await authApi.forgotPassword({ email: data.email });

            toast({
                title: 'Reset Link Sent!',
                description: response.message,
                variant: 'success',
            });

            // Don't redirect automatically - user needs to check email
            // In real implementation, user would click link in email to get to reset page

        } catch (error) {
            console.error('Forgot password error:', error);

            let errorMessage = 'Failed to send reset link. Please try again.';

            if (error instanceof ApiError) {
                errorMessage = error.message;
            }

            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <AuthLayout imageSrc="/forgot.jpg" imageAlt="Forgot Password" logoPosition="center">
            {/* Header */}
            <div className="mb-8 text-left">
                <h1 className="font-newsreader text-2xl font-semibold tracking-tight tracking-tight text-foreground mb-3">
                    Forgot Your Password?
                </h1>
                <p className="text-muted-foreground text-sm leading-relaxed">
                    Enter the email associated with your account and we'll send you a link to reset your password.
                </p>
            </div>

            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                >
                    {/* Email */}
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-foreground font-semibold text-sm">
                                    Email
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        type="email"
                                        placeholder="janesmith@gmail.com"
                                        {...field}
                                        className="mt-2 h-12 border-border focus:border-border-subtle focus:ring-ring rounded-xl text-base"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Send Reset Link Button */}
                    <div className="mt-8">
                        <Button
                            type="submit"
                            className="w-full bg-foreground-soft hover:bg-foreground active:bg-foreground text-primary-foreground disabled:opacity-50 h-12 rounded-xl text-base flex items-center justify-center gap-2 transition-all duration-200 "
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Sending Reset Link...
                                </>
                            ) : (
                                <>
                                    Send Reset Link
                                    <MailCheck className="w-5 h-5" />
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </Form>

            {/* Back to Login */}
            <div className="mt-4 text-center">
                <span className="text-muted-foreground text-sm">Remembered your credentials? </span>
                <Button
                    type="button"
                    variant="link"
                    onClick={handleBackToLogin}
                    className="text-foreground-soft hover:text-foreground hover:underline p-0 h-auto text-sm font-semibold transition-colors duration-200"
                >
                    Sign in instead
                </Button>
            </div>
        </AuthLayout>
    );
}
